<template>
  <div class="redemptions-table">
    <div class="row filters mb-4">
      <div class="col-2">
        <b-field>
          <b-select @input="load" v-model="filters.type" placeholder="Tipo">
            <option :value="undefined">Todos</option>
            <option v-for="(type, index) in types" :key="index" :value="index">
              {{ type }}
            </option>
          </b-select>
        </b-field>
      </div>
      <div class="col-2">
        <b-field required>
          <b-datepicker
            @input="load"
            v-model="datesFilter"
            :years-range="[-100, 100]"
            :date-formatter="bDateRangeFormatter"
            placeholder="Filtrar por período"
            trap-focus
            range
          >
            <button
              @click="
                datesFilter = null;
                load();
              "
              v-app-button.icon-left.clear
            >
              <b-icon size="is-small" icon="close"></b-icon>Limpar
            </button>
          </b-datepicker>
        </b-field>
      </div>
      <div class="col-2">
        <b-field>
          <app-autocomplete
            placeholder="Filtrar por Rede"
            field="name"
            :searchFunction="searchNetworkFn"
            :resourceIdFunction="resourceNetworkFn"
            v-model="filters.network_id"
            @selected="load"
          ></app-autocomplete>
        </b-field>
      </div>
      <div class="col-2">
        <app-input
          @input="onInput"
          v-model="filters.term"
          placeholder="Pesquisar"
        ></app-input>
      </div>
    </div>

    <b-table
      :data="data"
      :loading="isLoading"
      paginated
      backend-pagination
      :total="total"
      :per-page="perPage"
      @page-change="onPageChange"
      aria-next-label="Próxima Página"
      aria-previous-label="Página Anterior"
      aria-page-label="Página"
      aria-current-label="Página Atual"
    >
      <template slot-scope="props">
        <b-table-column label="Data">
          {{ props.row.created_at | date('DD/MM/YYYY HH:mm') }}
        </b-table-column>

        <b-table-column label="Tipo">
          {{ props.row.type }}
        </b-table-column>

        <b-table-column label="Rede">
          {{ props.row.client.network.name }}
        </b-table-column>
        <b-table-column label="Vendedor">
          <p>{{ props.row.name }}</p>
          <p>{{ props.row.client.cpf | VMask('###.###.###-##') }}</p>
        </b-table-column>
        <b-table-column label="Pontos">
          {{ props.row.points | number }}
        </b-table-column>

        <b-table-column label="Valor">
          R$ {{ props.row.value | number }}
        </b-table-column>

        <b-table-column label="Câmbio">
          1 / R$ {{ props.row.exchange_rate | number }}
        </b-table-column>

        <b-table-column label="Opções">
          <router-link
            :to="`${props.row.id}`"
            tag="button"
            type="button"
            v-app-button.icon-only
            title="Visualizar"
            v-tooltip.bottom="'Visualizar'"
          >
            <b-icon size="is-small" icon="magnify"></b-icon>
          </router-link>
          <button
            @click="onClickDeleteRedemption(props.row.id)"
            v-app-button.icon-only
            v-tooltip.bottom="'Excluir'"
            color="danger"
          >
            <b-icon size="is-small" icon="delete"></b-icon>
          </button>
        </b-table-column>
      </template>

      <template slot="empty">
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large"></b-icon>
            </p>
            <p>Nenhum registro encontrado.</p>
          </div>
        </section>
      </template>
    </b-table>
  </div>
</template>

<script>
import RedemptionService from '@/services/redemption.service';
import NetworkService from '@/services/network.service';
import BuefyMixin from '@/mixins/BuefyMixin';

import debounce from 'lodash/debounce';

export default {
  name: 'redemptions-table',
  mixins: [BuefyMixin],
  data: () => {
    return {
      isLoading: true,
      filters: {},
      datesFilter: null,
      data: [],
      total: 0,
      page: 1,
      perPage: 10,
      types: [],
    };
  },
  computed: {
    requestFilters() {
      const { term, network_id, type } = this.filters;
      const [start_date, end_date] = this.datesFilter || [];

      return { term, start_date, end_date, network_id, type };
    },
  },
  methods: {
    load() {
      RedemptionService.get({
        page: this.page,
        perPage: this.perPage,
        ...this.requestFilters,
      })
        .then((response) => {
          const { total, data } = response.data;

          this.total = total;
          this.data = data;
        })
        .catch(() => this.toastify('Erro ao carregar Resgates.', 'error'))
        .finally(() => (this.isLoading = false));
    },
    loadTypes() {
      RedemptionService.getTypes().then(({ data }) => (this.types = data));
    },
    onInput: debounce(function () {
      this.load();
    }, 400),
    onPageChange(page) {
      (this.page = page), this.load();
    },
    onClickDeleteRedemption(id) {
      this.$buefy.dialog.confirm({
        title: 'Excluindo Resgate',
        message:
          'Tem certeza que deseja <b>excluir</b> este resgate? Essa ação não pode ser desfeita.',
        cancelText: 'Cancelar',
        confirmText: 'Excluir Resgate',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deleteRedemption(id),
      });
    },
    deleteRedemption(id) {
      RedemptionService.delete(id)
        .then(() => {
          this.toastify('Resgate excluído com sucesso.');
          this.load();
        })
        .catch(({ response }) => {
          this.toastify(
            response.data.message || 'Erro ao tentar excluir Resgate.',
            'error'
          );
        });
    },
    searchNetworkFn: NetworkService.search,
    resourceNetworkFn: NetworkService.getId,
  },
  mounted() {
    this.filters = {
      ...this.filters,
      type: localStorage.redemptionsType,
    };

    if (localStorage.redemptionsTerm) {
      this.filters = {
        ...this.filters,
        term: localStorage.redemptionsTerm,
      };
    }

    if (localStorage.redemptionsNetworkId) {
      this.filters = {
        ...this.filters,
        network_id: localStorage.redemptionsNetworkId,
      };
    }

    if (localStorage.redemptionsDates) {
      const date = [
        new Date(localStorage.redemptionsTableFilterDates.split(',')[0]),
        new Date(localStorage.redemptionsTableFilterDates.split(',')[1]),
      ];
      this.datesFilter = date;
    }
    this.load();
    this.loadTypes();
  },
  watch: {
    datesFilter: (newValue) => {
      localStorage.redemptionsTableDates = newValue;
    },
    'filters.network_id': (newValue) => {
      localStorage.redemptionsTableNetworkId = newValue || '';
    },
    'filters.type': (newValue) => {
      localStorage.redemptionsTableType =
        typeof newValue === 'number' ? newValue : '';
    },
    'filters.term': (newValue) => {
      localStorage.redemptionsTableTerm = newValue;
    },
  },
};
</script>
